import * as React from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useTheme } from '@mui/material/styles';
import { ColorModeContext } from '../ColorModeContext';

const DEFAULT_CENTER = [14.6349, -90.5069];

const markerIcon = L.divIcon({
  className: 'location-picker-pin',
  html:
    '<div style="width:18px;height:18px;border-radius:50%;background:#238048;border:3px solid #ffffff;box-shadow:0 0 0 2px #238048,0 2px 6px rgba(0,0,0,0.4);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Esri Gray Canvas tiles — free, no API key, clean minimal look.
// Esri uses {z}/{y}/{x} tile order (not {z}/{x}/{y}).
// Base layer + reference layer (labels/boundaries) for each mode.
function ThemedTileLayer({ mode }) {
  const isDark = mode === 'dark';
  const baseUrl = isDark
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  const refUrl = isDark
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}';
  return (
    <>
      <TileLayer key={mode + '-base'} url={baseUrl} attribution="&copy; Esri" />
      <TileLayer key={mode + '-ref'} url={refUrl} />
    </>
  );
}

// Captures clicks on the map and reports them via onChange.
function ClickHandler({ onChange }) {
  useMapEvents({
    click: (e) => {
      onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

// Pans the map to the incoming value only when it changes from outside
// user interaction (drag/click). The lastEmittedRef is shared with the
// parent so we can distinguish external updates from our own emissions.
function Recenter({ value, lastEmittedRef }) {
  const map = useMap();
  React.useEffect(() => {
    if (!value) return;
    const last = lastEmittedRef.current;
    if (last && Math.abs(last.lat - value.lat) < 1e-9 && Math.abs(last.lng - value.lng) < 1e-9) {
      return;
    }
    map.flyTo([value.lat, value.lng], 15);
  }, [value, map, lastEmittedRef]);
  return null;
}

export default function LocationPickerMap({ value, onChange }) {
  const colorMode = React.useContext(ColorModeContext);
  const theme = useTheme();
  const mode = colorMode?.mode || theme.palette.mode || 'light';

  const [error, setError] = React.useState('');
  const lastEmittedRef = React.useRef(value ? { lat: value.lat, lng: value.lng } : null);

  const center = value ? [value.lat, value.lng] : DEFAULT_CENTER;
  const zoom = value ? 15 : 13;

  const emit = React.useCallback(
    (next) => {
      lastEmittedRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const handleUseLocation = () => {
    setError('');
    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalización.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        emit({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setError('No se pudo obtener tu ubicación. Verifica los permisos del navegador.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Arrastra el marcador o haz clic en el mapa para fijar la ubicación. También puedes usar tu ubicación actual.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          height: 320,
          borderRadius: 8,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <ThemedTileLayer mode={mode} />
          <ClickHandler onChange={emit} />
          <Recenter value={value} lastEmittedRef={lastEmittedRef} />
          {value && (
            <Marker
              position={[value.lat, value.lng]}
              icon={markerIcon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const ll = e.target.getLatLng();
                  emit({ lat: ll.lat, lng: ll.lng });
                },
              }}
            />
          )}
        </MapContainer>
      </Box>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        startIcon={<MyLocationIcon />}
        onClick={handleUseLocation}
        sx={{ mt: 1 }}
      >
        Usar mi ubicación
      </Button>
    </Box>
  );
}
